import type { ChartUnit } from '../../../core/types/chart-unit.js';
import { getUnitAsString } from '../../../core/utils/get-unit-as-string.js';
import { XYChart } from '../../../xy-chart/XYChartRoot.js';
import type {
  XYAreaSeriesActions,
  XYBarSeriesActions,
  XYLineSeriesActions,
} from '../map-series-actions.js';
import type { SeriesProps } from '../transformer-data-to-xy.js';

export const xAxisId = 'x-axis';

export const defineAxisId = (unit: ChartUnit) =>
  `y-axis-${getUnitAsString(unit)}`;

const baseSeriesProps = ({ data, unit, seriesId }: SeriesProps) => ({
  data,
  xAxisId,
  yAxisId: defineAxisId(unit),
  x0Accessor: 'start',
  x1Accessor: 'end',
  seriesIdAccessor: 'seriesId',
  nameAccessor: 'seriesName',
  key: seriesId,
});

type LineSeriesOptions = { actions?: XYLineSeriesActions };
type AreaSeriesOptions = { actions?: XYAreaSeriesActions };
type BarSeriesOptions = { actions?: XYBarSeriesActions };
type BandSeriesOptions = { actions?: XYAreaSeriesActions };

export const renderLineSeries = (
  series: SeriesProps,
  options: LineSeriesOptions = {},
) => (
  <XYChart.LineSeries
    {...baseSeriesProps(series)}
    y0Accessor="value"
    color={series.color}
    pointsDisplay={series.pointsDisplay}
    actions={options.actions}
  />
);

export const renderAreaSeries = (
  series: SeriesProps,
  options: AreaSeriesOptions = {},
) => (
  <XYChart.AreaSeries
    {...baseSeriesProps(series)}
    y1Accessor="value"
    color={series.color}
    pointsDisplay={series.pointsDisplay}
    actions={options.actions}
  />
);

export const renderBarSeries = (
  series: SeriesProps,
  options: BarSeriesOptions = {},
) => (
  <XYChart.BarSeries
    {...baseSeriesProps(series)}
    variant="stack"
    y1Accessor="value"
    color={series.color}
    actions={options.actions}
  />
);

export const renderBandSeries = (
  series: SeriesProps,
  options: BandSeriesOptions = {},
) => (
  <XYChart.AreaSeries
    {...baseSeriesProps(series)}
    y0Accessor="y0"
    y1Accessor="y1"
    color={series.color}
    actions={options.actions}
  />
);
